import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { LifecycleBadge, lifecycleLabel } from '@siliconx/shared/ui/LifecycleBadge';
import { formatDate } from '../../lib/format';
import { addDaysISO, addMonthsISO, endOfDayISO } from '../../lib/dates';
import { DateField } from '../shared/DateField';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import type { LifecycleAdvanceResult } from '@siliconx/shared';
import { DIRECTIONAL_PRICE_ONLY_MESSAGE } from '@siliconx/shared';
import { assetIsListable } from '../asset-v2/shared';
import type { ApiError, SellerMetadata } from './asset-detail-types';
import { useOrgFeature } from '../../lib/orgFeatures';
// SIL-1170 PROTOTYPE — throwaway. Delete this import and its call site below
// once a variant is picked; see ListingEditorSellerFieldsPrototype.tsx.
import { ListingEditorSellerFieldsPrototype } from './ListingEditorSellerFieldsPrototype';

/**
 * SIL-565: asset-detail cards that are owned/fetched by the route but shared
 * by BOTH render branches (v2 default and `?v=1` legacy) — split out of the
 * route file (routes/assets/$assetId.tsx) so it isn't the largest file in
 * the app. Content and behavior unchanged from the prior inline render.
 */

// ---------------------------------------------------------------------------
// Seller details card (RUF-279) — editable by the seller
// ---------------------------------------------------------------------------

interface SellerDetailsCardProps {
  assetId: string;
  initial: SellerMetadata | null;
}

export function SellerDetailsCard({ assetId, initial }: SellerDetailsCardProps) {
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [priceStr, setPriceStr] = useState(
    initial?.purchasePriceUsd != null ? String(initial.purchasePriceUsd) : '',
  );
  const [purchaseDate, setPurchaseDate] = useState(initial?.purchaseDate ?? '');
  const [location, setLocation] = useState(initial?.internalLocation ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (payload: {
      purchasePriceUsd: number | null;
      purchaseDate: string | null;
      internalLocation: string | null;
      notes: string | null;
    }) => {
      const res = await api.api.client.assets[':assetId'].metadata.$put({
        param: { assetId },
        json: payload,
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) });
      setEditing(false);
      setSaveError(null);
    },
    onError: (err: Error) => {
      setSaveError(err.message ?? 'Save failed.');
    },
  });

  function handleSave() {
    const parsedPrice = priceStr.trim() !== '' ? Number(priceStr) : null;
    if (parsedPrice !== null && (isNaN(parsedPrice) || parsedPrice <= 0)) {
      setSaveError('Purchase price must be a positive number.');
      return;
    }
    setSaveError(null);
    mutation.mutate({
      purchasePriceUsd: parsedPrice,
      purchaseDate: purchaseDate.trim() !== '' ? purchaseDate.trim() : null,
      internalLocation: location.trim() !== '' ? location.trim() : null,
      notes: notes.trim() !== '' ? notes.trim() : null,
    });
  }

  function handleCancel() {
    setPriceStr(initial?.purchasePriceUsd != null ? String(initial.purchasePriceUsd) : '');
    setPurchaseDate(initial?.purchaseDate ?? '');
    setLocation(initial?.internalLocation ?? '');
    setNotes(initial?.notes ?? '');
    setSaveError(null);
    setEditing(false);
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <PanelTitle title="Seller details" />
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Purchase price (USD)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={priceStr}
                onChange={(e) => setPriceStr(e.target.value)}
                placeholder="e.g. 4200.00"
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Purchase date
              </label>
              <input
                type="date"
                value={purchaseDate}
                min="1990-01-01"
                max="2100-12-31"
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Internal location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Rack A3, Shelf 2"
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes about this asset…"
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {saveError && <p className="text-sm text-red-600">{saveError}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={mutation.isPending}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={mutation.isPending}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Purchase price
            </dt>
            <dd className="mt-1">
              {initial?.purchasePriceUsd != null ? (
                <UsdCell value={initial.purchasePriceUsd} className="text-sm" />
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Purchase date
            </dt>
            <dd className="mt-1 text-sm text-foreground">
              {initial?.purchaseDate ? formatDate(initial.purchaseDate) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Internal location
            </dt>
            <dd className="mt-1 text-sm text-foreground">{initial?.internalLocation ?? '—'}</dd>
          </div>
          {initial?.notes && (
            <div className="col-span-2 sm:col-span-3">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes</dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-foreground">{initial.notes}</dd>
            </div>
          )}
        </dl>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Location + managing team + notes + asset tag card (RUF-448, SIL-274)
//
// Admin-editable; a member without asset-edit permission (role !== 'admin')
// sees a read-only view (mirrors the admin gate on PUT .../details and PUT
// /api/client/teams/asset/:assetId). Location comes from the org's own data
// centers (GET /api/client/locations, RLS-scoped); team from GET
// /api/client/teams. Location + notes + asset tag save via PUT .../details
// (client_set_asset_location / client_set_asset_notes, SECURITY DEFINER, 570;
// client_set_asset_tag, 692); team saves via the existing
// PUT /api/client/teams/asset/:assetId (client_assign_asset_team, 554).
//
// SIL-1036: Location is `orgFeature: 'locations'` and managing team is
// `orgFeature: 'teams'` (both business-only — an individual customer_org has
// no data-center concept and no team/board). Both rows (and the location's
// edit-mode `GET /api/client/locations` fetch, which 403'd for individuals
// before this) are gated; asset tag + notes stay for every org type. When
// BOTH rows are hidden the card has nothing but notes left to title, so the
// heading itself becomes "Notes".
// ---------------------------------------------------------------------------

interface LocationOption {
  id: string;
  code: string;
  name: string;
}

interface TeamOption {
  id: string;
  name: string;
}

interface LocationTeamNotesCardProps {
  assetId: string;
  dataCenterId: string | null;
  dataCenterName: string | null;
  teamId: string | null;
  teamName: string | null;
  notes: string | null;
  assetTag: string | null;
  canEdit: boolean;
}

export function LocationTeamNotesCard({
  assetId,
  dataCenterId,
  dataCenterName,
  teamId,
  teamName,
  notes,
  assetTag,
  canEdit,
}: LocationTeamNotesCardProps) {
  const queryClient = useQueryClient();
  const canLocations = useOrgFeature('locations');
  const canTeams = useOrgFeature('teams');
  const [editing, setEditing] = useState(false);
  const [selectedDataCenterId, setSelectedDataCenterId] = useState(dataCenterId ?? '');
  const [selectedTeamId, setSelectedTeamId] = useState(teamId ?? '');
  const [notesDraft, setNotesDraft] = useState(notes ?? '');
  const [assetTagDraft, setAssetTagDraft] = useState(assetTag ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: locations } = useQuery<LocationOption[]>({
    queryKey: qk.locations(),
    queryFn: async () => {
      const res = await api.api.client.locations.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LocationOption[];
    },
    enabled: canEdit && editing && canLocations,
  });
  const { data: teams } = useQuery<TeamOption[]>({
    queryKey: qk.teams(),
    queryFn: async () => {
      const res = await api.api.client.teams.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TeamOption[];
    },
    enabled: canEdit && editing && canTeams,
  });

  const detailsMutation = useMutation({
    mutationFn: async (payload: {
      dataCenterId: string | null;
      notes: string | null;
      assetTag: string | null;
    }) => {
      const res = await api.api.client.assets[':assetId'].details.$put({
        param: { assetId },
        json: payload,
      });
      await throwIfNotOk(res);
    },
  });
  const teamMutation = useMutation({
    mutationFn: async (payload: { teamId: string | null }) => {
      const res = await api.api.client.teams.asset[':assetId'].$put({
        param: { assetId },
        json: payload,
      });
      await throwIfNotOk(res);
    },
  });

  async function handleSave() {
    setSaveError(null);
    try {
      await detailsMutation.mutateAsync({
        dataCenterId: selectedDataCenterId || null,
        notes: notesDraft.trim() !== '' ? notesDraft : null,
        assetTag: assetTagDraft.trim() !== '' ? assetTagDraft.trim() : null,
      });
      if (canTeams && (selectedTeamId || null) !== teamId) {
        await teamMutation.mutateAsync({ teamId: selectedTeamId || null });
      }
      void queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) });
      void queryClient.invalidateQueries({ queryKey: qk.assets.listAll() });
      setEditing(false);
    } catch (err) {
      setSaveError((err as Error).message ?? 'Save failed.');
    }
  }

  function handleCancel() {
    setSelectedDataCenterId(dataCenterId ?? '');
    setSelectedTeamId(teamId ?? '');
    setNotesDraft(notes ?? '');
    setAssetTagDraft(assetTag ?? '');
    setSaveError(null);
    setEditing(false);
  }

  const saving = detailsMutation.isPending || teamMutation.isPending;
  // SIL-1036: neither row applies to an individual customer_org — the card
  // is left with only notes/asset-tag content, so it's titled accordingly.
  const cardTitle = canLocations || canTeams ? 'Location & team' : 'Notes';

  return (
    <Card>
      <div className="flex items-center justify-between">
        <PanelTitle title={cardTitle} />
        {canEdit && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {canLocations && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Location
                </label>
                <select
                  value={selectedDataCenterId}
                  onChange={(e) => setSelectedDataCenterId(e.target.value)}
                  className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
                >
                  <option value="">— none —</option>
                  {(locations ?? []).map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}
            {canTeams && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Managing team
                </label>
                <select
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
                >
                  <option value="">— none —</option>
                  {(teams ?? []).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Asset tag
              </label>
              <input
                type="text"
                value={assetTagDraft}
                onChange={(e) => setAssetTagDraft(e.target.value)}
                maxLength={128}
                placeholder="Asset tag"
                aria-label="Edit asset tag"
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={3}
                placeholder="Notes about this asset…"
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {saveError && <p className="text-sm text-red-600">{saveError}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          {canLocations && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Location
              </dt>
              <dd className="mt-1 text-sm text-foreground">{dataCenterName ?? '—'}</dd>
            </div>
          )}
          {canTeams && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Managing team
              </dt>
              <dd className="mt-1 text-sm text-foreground">{teamName ?? '—'}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Asset tag
            </dt>
            <dd className="mt-1 text-sm text-foreground">{assetTag ?? '—'}</dd>
          </div>
          {notes && (
            <div className="col-span-2 sm:col-span-3">
              <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes</dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-foreground">{notes}</dd>
            </div>
          )}
        </dl>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Lifecycle advance button (RUF-280)
// Shown when asset is active or idle; hidden when decommissioned or other states.
// ---------------------------------------------------------------------------

const ADVANCE_CONFIG: Record<string, { label: string; confirmText: string }> = {
  active: {
    label: 'Take out of production',
    confirmText:
      'This will move the asset to Idle. It may be relisted on the exchange. Proceed?',
  },
  idle: {
    label: 'Decommission',
    confirmText:
      'This will permanently decommission the asset. This action cannot be undone. Proceed?',
  },
};

const LIFECYCLE_STAGE_OPTIONS = [
  'active',
  'idle',
  'in_transit',
  'decommissioned',
  'disposed',
  'quarantined',
];

interface LifecycleAdvanceButtonProps {
  assetId: string;
  lifecycleStateId: string | null;
  canEdit: boolean;
}

export function LifecycleAdvanceButton({ assetId, lifecycleStateId, canEdit }: LifecycleAdvanceButtonProps) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [editingStage, setEditingStage] = useState(false);
  const [pendingStage, setPendingStage] = useState(lifecycleStateId ?? 'active');

  const config = lifecycleStateId ? ADVANCE_CONFIG[lifecycleStateId] : undefined;

  const advanceMutation = useMutation<
    LifecycleAdvanceResult,
    Error & { status?: number; body?: unknown }
  >({
    mutationFn: async () => {
      const res = await api.api.client.assets[':assetId'].lifecycle.advance.$post({
        param: { assetId },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as LifecycleAdvanceResult;
    },
    onSuccess: () => {
      setConfirming(false);
      setInlineError(null);
      toast.success('Asset lifecycle updated.');
      void queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) });
    },
    onError: (err) => {
      setConfirming(false);
      if (err.status === 409) {
        // Surface the human-readable rejection reason inline
        setInlineError(err.message);
      } else {
        setInlineError(null);
        toast.error(err.message ?? 'Lifecycle advance failed.');
      }
    },
  });

  const setStageMutation = useMutation<
    { id: string; lifecycleStateId: string },
    Error & { status?: number },
    string
  >({
    mutationFn: async (stage: string) => {
      const res = await api.api.client.assets[':assetId']['lifecycle-state'].$patch({
        param: { assetId },
        json: {
          lifecycleStateId: stage as
            | 'active'
            | 'idle'
            | 'in_transit'
            | 'decommissioned'
            | 'disposed'
            | 'quarantined',
        },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { id: string; lifecycleStateId: string };
    },
    onSuccess: () => {
      setEditingStage(false);
      toast.success('Lifecycle stage updated.');
      void queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) });
    },
    onError: (err) => toast.error(err.message ?? 'Failed to update lifecycle stage.'),
  });

  if (!config && !canEdit) return null; // nothing to show

  return (
    <Card>
      <PanelTitle title="Lifecycle management" />

      {/* Current stage — visible to every role, not just editors. */}
      <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Current stage
        </span>
        {lifecycleStateId ? (
          <LifecycleBadge id={lifecycleStateId} label={lifecycleLabel(lifecycleStateId)} />
        ) : (
          <span className="text-muted-foreground">Not set</span>
        )}
      </div>

      {canEdit && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {editingStage ? (
            <>
              <select
                className="cursor-pointer rounded-md border border-border bg-card px-2 py-1.5 text-sm disabled:cursor-not-allowed"
                value={pendingStage}
                disabled={setStageMutation.isPending}
                onChange={(e) => setPendingStage(e.target.value)}
                aria-label="Set lifecycle stage"
              >
                {LIFECYCLE_STAGE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {lifecycleLabel(s)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setStageMutation.mutate(pendingStage)}
                disabled={setStageMutation.isPending || pendingStage === (lifecycleStateId ?? 'active')}
                className="rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {setStageMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditingStage(false)}
                disabled={setStageMutation.isPending}
                className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setPendingStage(lifecycleStateId ?? 'active');
                setEditingStage(true);
              }}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              Set lifecycle stage
            </button>
          )}
        </div>
      )}

      {inlineError && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {inlineError}
        </div>
      )}

      {config && (
        <>
          {!confirming ? (
            <div className="mt-4">
              {/* SIL-337: a solid near-white fill here out-ranked the emerald
                  "List for sale" card next to it, so the decommissioning path
                  visually beat the sale path on a page whose purpose is a
                  direct route to sale. Demoted to the DESIGN.md secondary
                  treatment (bordered, Instrument White) — same non-committing
                  weight as "Set lifecycle stage" above; the confirm step below
                  keeps its distinct red commit action. */}
              <Button variant="secondary"
                onClick={() => {
                  setInlineError(null);
                  setConfirming(true);
                }}
              >
                {config.label}
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">{config.confirmText}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => advanceMutation.mutate()}
                  disabled={advanceMutation.isPending}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {advanceMutation.isPending ? 'Processing…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={advanceMutation.isPending}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// List for sale card (RUF-308)
// Shown only when the asset's lifecycle state allows listing. Pre-fills the
// asking price with the asset's latest FMV median (editable).
// ---------------------------------------------------------------------------

interface ListForSaleCardProps {
  assetId: string;
  allowsListing: boolean;
  /** 669: in-service ('active' lifecycle) assets are forward-listable — the
   * card stays visible but REQUIRES a future availability date. */
  lifecycleStateId: string | null;
  fmvMedian: number | null;
  /** SIL-1848: the FMV median is a directional (virtual) estimate - no price prefill. */
  fmvDirectional?: boolean;
  lotId: string | null;
  lotName: string | null;
}

// 669: friendly copy for the create-listing 409/422 forward-availability codes.
const CREATE_LISTING_ERROR_MESSAGES: Record<string, string> = {
  lifecycle_not_listable:
    'This asset is still in service — choose a future availability date to list it as a forward sale.',
  invalid_available_on: 'The availability date must be in the future and at most 12 months out.',
  invalid_expires_at: 'The expiration date must be in the future.',
};

export function ListForSaleCard({
  assetId,
  allowsListing,
  lifecycleStateId,
  fmvMedian,
  fmvDirectional = false,
  lotId,
  lotName,
}: ListForSaleCardProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // SIL-1848: a directional median is never offered as a default price.
  const defaultMedian = fmvDirectional ? null : fmvMedian;
  const [priceStr, setPriceStr] = useState(defaultMedian != null ? String(defaultMedian) : '');
  const [availableOn, setAvailableOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [done, setDone] = useState(false);
  // SIL-643: sale mechanic. 'buy_now' preserves today's flow exactly; picking
  // 'auction' swaps the asking-price field for reserve/increment/close/visibility.
  const [saleMechanic, setSaleMechanic] = useState<'buy_now' | 'auction'>('buy_now');
  const [reservePriceStr, setReservePriceStr] = useState('');
  const [minIncrementStr, setMinIncrementStr] = useState('');
  const [closesAtLocal, setClosesAtLocal] = useState(''); // datetime-local value
  const [bidVisibility, setBidVisibility] = useState<'open' | 'sealed'>('open');

  // 669: an in-service asset isn't listable spot but IS listable forward —
  // availability becomes visibly required the moment this card renders for it.
  const inService = !allowsListing && lifecycleStateId === 'active';

  const mutation = useMutation({
    mutationFn: async (payload: {
      assetId: string;
      askingPriceUsd?: number;
      availableOn?: string;
      expiresAt?: string;
      saleMechanic?: 'buy_now' | 'auction';
      auction?: {
        reservePriceUsd: number;
        minIncrementUsd: number;
        closesAt: string;
        bidVisibility: 'open' | 'sealed';
      };
    }) => {
      const res = await api.api.client.listings.$post({ json: payload });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      setDone(true);
      setOpen(false);
      toast.success('Listing created as a draft. Activate it to publish to the exchange.');
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error & { body?: { error?: { code?: string } } }) => {
      const code = err.body?.error?.code;
      toast.error(
        (code && CREATE_LISTING_ERROR_MESSAGES[code]) ?? err.message ?? 'Could not create listing.',
      );
    },
  });

  // SIL-337: render gate now delegates to the shared `assetIsListable` check
  // (also used for the Lifecycle timeline's "Next step" node) rather than a
  // parallel `allowsListing || inService` derivation — one listability rule,
  // not two that can drift.
  if (!assetIsListable({ lifecycleAllowsListing: allowsListing, lifecycleStateId })) return null;

  // Lot members are sold via their lot listing — independent listing is
  // blocked server-side (asset_in_lot 409); explain instead of offering it.
  if (lotId != null) {
    return (
      <Card className="scroll-mt-6" id="list-for-sale">
        <PanelTitle title="List for sale" />
        <p className="mt-4 text-sm text-muted-foreground">
          This asset belongs to the lot{' '}
          <Link to="/lots/$lotId" params={{ lotId }} search={{ tab: 'overview' }} className="text-blue-600 hover:text-blue-800">
            {lotName ?? 'view lot'}
          </Link>{' '}
          and can only be sold as part of that lot. Remove it from the lot to list it independently.
        </p>
      </Card>
    );
  }

  function handleCreate() {
    if (inService && !availableOn) {
      toast.error('This asset is still in service — choose when it’ll be available.');
      return;
    }

    if (saleMechanic === 'auction') {
      const reserve = Number(reservePriceStr);
      const increment = Number(minIncrementStr);
      if (reservePriceStr.trim() === '' || isNaN(reserve) || reserve <= 0) {
        toast.error('Reserve price must be a positive number.');
        return;
      }
      if (minIncrementStr.trim() === '' || isNaN(increment) || increment <= 0) {
        toast.error('Minimum bid increment must be a positive number.');
        return;
      }
      if (!closesAtLocal) {
        toast.error('Choose when the auction closes.');
        return;
      }
      const closesAtIso = new Date(closesAtLocal).toISOString();
      if (new Date(closesAtIso).getTime() <= Date.now()) {
        toast.error('Auction close time must be in the future.');
        return;
      }
      mutation.mutate({
        assetId,
        availableOn: availableOn || undefined,
        expiresAt: expiresOn ? endOfDayISO(expiresOn) : undefined,
        saleMechanic: 'auction',
        auction: {
          reservePriceUsd: reserve,
          minIncrementUsd: increment,
          closesAt: closesAtIso,
          bidVisibility,
        },
      });
      return;
    }

    const parsed = priceStr.trim() !== '' ? Number(priceStr) : undefined;
    if (parsed !== undefined && (isNaN(parsed) || parsed <= 0)) {
      toast.error('Asking price must be a positive number.');
      return;
    }
    mutation.mutate({
      assetId,
      askingPriceUsd: parsed,
      availableOn: availableOn || undefined,
      expiresAt: expiresOn ? endOfDayISO(expiresOn) : undefined,
      saleMechanic: 'buy_now',
    });
  }

  return (
    <Card className="scroll-mt-6" id="list-for-sale">
      <PanelTitle title="List for sale" />

      {done ? (
        <div className="mt-4 space-y-2">
          <p className="text-sm text-muted-foreground">
            Draft listing created.{' '}
            <Link to="/listings" className="text-blue-600 hover:text-blue-800">
              Go to My listings
            </Link>{' '}
            to activate it.
          </p>
        </div>
      ) : !open ? (
        <div className="mt-4">
          <p className="text-sm text-muted-foreground">
            {inService
              ? 'This asset is still in service — it can be listed as a forward sale with a future availability date.'
              : 'This asset is eligible to be listed on the exchange.'}
          </p>
          <Button className="mt-3"
            onClick={() => setOpen(true)}
          >
            List for sale
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {defaultMedian != null && (
            <div className="text-sm text-muted-foreground">
              FMV median:{' '}
              <UsdCell value={defaultMedian} className="font-medium text-foreground" />
              <span className="text-muted-foreground"> — your pricing anchor</span>
            </div>
          )}
          {fmvDirectional && (
            <div className="text-sm text-amber-700" data-testid="directional-no-default-price">
              {DIRECTIONAL_PRICE_ONLY_MESSAGE}
            </div>
          )}

          {/* SIL-643: sale mechanic. buy_now is the existing flow, unchanged
              below when selected. */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Sale mechanic
            </label>
            <div className="mt-1 flex gap-2" role="radiogroup" aria-label="Sale mechanic">
              {(['buy_now', 'auction'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={saleMechanic === m}
                  onClick={() => setSaleMechanic(m)}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    saleMechanic === m
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {m === 'buy_now' ? 'Buy now' : 'Auction'}
                </button>
              ))}
            </div>
          </div>

          {saleMechanic === 'buy_now' ? (
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Asking price (USD)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={priceStr}
                onChange={(e) => setPriceStr(e.target.value)}
                placeholder="e.g. 4200.00"
                className="mt-1 w-full max-w-xs rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Pre-filled with the latest FMV median. Leave as-is or override.
              </p>
            </div>
          ) : (
            <div className="grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Reserve price (USD)
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={reservePriceStr}
                  onChange={(e) => setReservePriceStr(e.target.value)}
                  placeholder="e.g. 4200.00"
                  className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Private — never shown to buyers. The floor below which you won't sell.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Minimum bid increment (USD)
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={minIncrementStr}
                  onChange={(e) => setMinIncrementStr(e.target.value)}
                  placeholder="e.g. 100.00"
                  className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Auction closes
                </label>
                <input
                  type="datetime-local"
                  value={closesAtLocal}
                  onChange={(e) => setClosesAtLocal(e.target.value)}
                  className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Bid visibility
                </label>
                <select
                  value={bidVisibility}
                  onChange={(e) => setBidVisibility(e.target.value as 'open' | 'sealed')}
                  className="mt-1 w-full rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
                >
                  <option value="open">Open — bidders see the current high bid</option>
                  <option value="sealed">Sealed — bids are private until close</option>
                </select>
              </div>
            </div>
          )}

          <div className="grid max-w-lg grid-cols-1 gap-4 sm:grid-cols-2">
            <DateField
              label="Availability date"
              value={availableOn}
              onChange={setAvailableOn}
              min={addDaysISO(1)}
              max={addMonthsISO(12)}
              required={inService}
              helper={
                inService
                  ? 'This asset is still in service — choose when it’ll be available.'
                  : 'Optional — leave blank to sell as available now. Up to 12 months out.'
              }
            />
            <DateField
              label="Expiration"
              value={expiresOn}
              onChange={setExpiresOn}
              min={addDaysISO(1)}
              helper="Optional — the listing expires at the end of this day if unsold."
            />
          </div>

          {/* SIL-1170 PROTOTYPE — six new buyer-facing seller fields + a live
              net-proceeds calc, three switchable variants (?listingVariant=
              A|B|C). Throwaway: see NOTES.md next to
              ListingEditorSellerFieldsPrototype.tsx. */}
          <ListingEditorSellerFieldsPrototype
            askingPriceUsd={
              saleMechanic === 'auction'
                ? Number(reservePriceStr) || defaultMedian || 0
                : Number(priceStr) || defaultMedian || 0
            }
          />

          <div className="flex items-center gap-3">
            <Button
              onClick={handleCreate}
              disabled={mutation.isPending || (inService && !availableOn)}
            >
              {mutation.isPending ? 'Creating…' : 'Create listing'}
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Attachments (RUF-449) — certs/warranty docs via presigned-URL flow.
// Mirrors the org-logo two-phase presign → PUT → finalize flow (profile.tsx
// BrandingSection), but unlike the logo, delete also removes the S3 object
// (server-side; no client action needed beyond the DELETE call).
// ---------------------------------------------------------------------------

const ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

interface AttachmentRow {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  createdAt: string;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsSection({ assetId }: { assetId: string }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery<{ data: AttachmentRow[] }, ApiError>({
    queryKey: qk.assets.attachments(assetId),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId'].attachments.$get({ param: { assetId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: AttachmentRow[] };
    },
  });

  const rows = data?.data ?? [];

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    if (!ATTACHMENT_TYPES.includes(file.type)) {
      toast.error('Attachment must be a PDF, JPEG, or PNG file');
      return;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      toast.error('Attachment must be 20 MB or smaller');
      return;
    }
    setBusy(true);
    try {
      // 1. presign
      const presignRes = await api.api.client.assets[':assetId'].attachments.$post({
        param: { assetId },
        json: { fileName: file.name, contentType: file.type, contentLength: file.size },
      });
      await throwIfNotOk(presignRes);
      const { uploadUrl, s3Key } = (await presignRes.json()) as unknown as {
        uploadUrl: string;
        s3Key: string;
      };
      // 2. PUT the raw bytes straight to S3 (no auth header on the presigned URL).
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);
      // 3. finalize → persists the row.
      const finalizeRes = await api.api.client.assets[':assetId'].attachments.finalize.$post({
        param: { assetId },
        json: { s3Key, fileName: file.name, contentType: file.type, sizeBytes: file.size },
      });
      await throwIfNotOk(finalizeRes);
      toast.success('Attachment uploaded');
      void queryClient.invalidateQueries({ queryKey: qk.assets.attachments(assetId) });
    } catch (ex: unknown) {
      toast.error((ex as Error).message ?? 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(attachmentId: string) {
    try {
      const res = await api.api.client.assets[':assetId'].attachments[':attachmentId'].download.$get({
        param: { assetId, attachmentId },
      });
      await throwIfNotOk(res);
      const { downloadUrl } = (await res.json()) as unknown as { downloadUrl: string };
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (ex: unknown) {
      toast.error((ex as Error).message ?? 'Could not fetch download link');
    }
  }

  async function handleDelete(attachmentId: string, fileName: string) {
    if (!confirm(`Delete attachment "${fileName}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await api.api.client.assets[':assetId'].attachments[':attachmentId'].$delete({
        param: { assetId, attachmentId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Attachment deleted');
      void queryClient.invalidateQueries({ queryKey: qk.assets.attachments(assetId) });
    } catch (ex: unknown) {
      toast.error((ex as Error).message ?? 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <PanelTitle title="Attachments" />
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={handleFile}
          />
          <Button size="sm" className="text-sm"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? 'Working…' : 'Upload'}
          </Button>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Certs, warranty docs. PDF, JPEG, or PNG up to 20 MB.
      </p>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No attachments yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-4 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{r.fileName}</p>
                <p className="text-xs text-muted-foreground">
                  {formatBytes(r.sizeBytes)} · {formatDate(r.createdAt)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleDownload(r.id)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Download
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleDelete(r.id, r.fileName)}
                  className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ownership provenance (RUF-313)
// ---------------------------------------------------------------------------

interface ProvenanceEvent {
  id: string;
  originalAssetId: string;
  clonedAssetId: string;
  fromCustomerOrgId: string;
  toCustomerOrgId: string;
  listingId: string | null;
  amountUsd: number | null;
  transferredAt: string | null;
  direction: 'acquired' | 'sold' | 'other';
}

interface ProvenanceResponse {
  assetId: string;
  previousAssetId: string | null;
  events: ProvenanceEvent[];
}

export function ProvenanceSection({ assetId }: { assetId: string }) {
  const { data } = useQuery<ProvenanceResponse, ApiError>({
    queryKey: qk.assets.provenance(assetId),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId'].provenance.$get({ param: { assetId } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as ProvenanceResponse;
    },
    retry: (failCount, err) => err.status !== 404 && failCount < 2,
  });

  // Nothing to show for an asset with no lineage and no transfer events.
  if (!data || (!data.previousAssetId && data.events.length === 0)) {
    return null;
  }

  return (
    <Card>
      <PanelTitle title="Ownership history" />
      <p className="mt-1 text-sm text-muted-foreground">
        Verifiable chain of custody for this unit. Each transfer is recorded immutably.
      </p>

      {data.previousAssetId && (
        <p className="mt-3 text-sm text-muted-foreground">
          This unit was created by an exchange transfer from a predecessor asset
          {' '}
          <span className="font-mono text-xs text-muted-foreground">
            {data.previousAssetId.slice(0, 8)}
          </span>
          .
        </p>
      )}

      <ol className="mt-4 space-y-3">
        {data.events.map((e) => {
          const label =
            e.direction === 'acquired'
              ? 'Acquired via exchange transfer'
              : e.direction === 'sold'
                ? 'Sold via exchange transfer'
                : 'Exchange transfer';
          return (
            <li
              key={e.id}
              className="flex items-start justify-between gap-4 rounded-md border border-border bg-muted px-4 py-3"
            >
              <div>
                <div className="text-sm font-medium text-foreground">{label}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {e.transferredAt ? formatDate(e.transferredAt) : '—'}
                  {' · '}
                  original{' '}
                  <span className="font-mono">{e.originalAssetId.slice(0, 8)}</span>
                  {' → '}clone{' '}
                  <span className="font-mono">{e.clonedAssetId.slice(0, 8)}</span>
                </div>
              </div>
              <div className="shrink-0 text-sm font-medium text-foreground">
                {e.amountUsd !== null
                  ? e.amountUsd.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                    })
                  : '—'}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
